import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  MultiPolygon,
  Point,
  Polygon,
} from "geojson";
import { featureCollection, pointOnFeature, bbox } from "@turf/turf";
import {
  MAP_CONFIG,
  LABEL_EXCLUDED_IDS,
  LABEL_EXCLUDED_ROOMS,
  LABEL_EXCLUDED_CATEGORIES,
  DIGITAL_SIGNAGE_LOCATION,
  DEFAULT_ROOM_LABEL_TEXT_SIZE,
  LOW_PRIORITY_LABEL_MIN_ZOOM,
} from "../config/constants";

type CustomGeoJsonProperties = GeoJsonProperties & {
  shapeType?: string;
  room?: string;
  id?: string;
  category?: string;
  suppress_label?: boolean;
};

type LabelPointProperties = CustomGeoJsonProperties & {
  labelTextSize?: number;
  labelMinZoom?: number;
  isLowPriority?: boolean;
  displayRoom?: string;
};

type LabelPointFeature = Feature<Point, LabelPointProperties>;

const LABEL_EXCLUDED_ID_SET = new Set<string>(LABEL_EXCLUDED_IDS);
const LABEL_EXCLUDED_ROOM_SET = new Set<string>(LABEL_EXCLUDED_ROOMS);
const LABEL_EXCLUDED_CATEGORY_SET = new Set<string>(LABEL_EXCLUDED_CATEGORIES);

type BoundsPaddingOptions = {
  ratioX?: number;
  ratioY?: number;
};

/**
 * マップ表示用の設定値を計算
 */
export const calculateMapConfig = () => {
  const originalWidth = MAP_CONFIG.maxX - MAP_CONFIG.minX;
  const originalHeight = MAP_CONFIG.maxY - MAP_CONFIG.minY;

  const scaleFactor = MAP_CONFIG.targetWidth / originalWidth;

  // デフォルトの表示位置をデジタルサイネージの座標に設定
  const scaledCenter: [number, number] = [
    DIGITAL_SIGNAGE_LOCATION.position.x,
    DIGITAL_SIGNAGE_LOCATION.position.y,
  ];

  return { scaleFactor, scaledCenter, originalWidth, originalHeight };
};

/**
 * GeoJSONの座標を表示用にスケール変換
 */
export const transformGeoJSONCoords = (
  geojson: FeatureCollection,
  scaleFactor: number,
): FeatureCollection => {
  const transformedFeatures = geojson.features.map((feature) => {
    const newGeometry = JSON.parse(JSON.stringify(feature.geometry));

    /**
     * 座標配列を再帰的に変換
     */
    const transformCoordinates = (
      coords: number[] | number[][] | number[][][],
    ): number[] | number[][] | number[][][] => {
      if (Array.isArray(coords)) {
        if (coords.length === 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
          return [
            (coords[0] - MAP_CONFIG.minX) * scaleFactor,
            (coords[1] - MAP_CONFIG.minY) * scaleFactor,
          ];
        }
        return coords.map((coord) => transformCoordinates(coord as number[] | number[][])) as
          | number[][]
          | number[][][];
      }
      return coords;
    };

    newGeometry.coordinates = transformCoordinates(newGeometry.coordinates);

    return { ...feature, geometry: newGeometry };
  });

  return { ...geojson, features: transformedFeatures };
};

/**
 * ポリゴンからラベル用のポイントデータを生成
 */
export const createLabelPoints = (
  polygons: FeatureCollection<Polygon | MultiPolygon>,
): FeatureCollection => {
  const detailPoints: LabelPointFeature[] = polygons.features
    .filter((polygonFeature) => {
      // ラベル表示対象をフィルタリング
      const properties = polygonFeature.properties as CustomGeoJsonProperties;
      if (!properties) return false;

      // ポリゴン以外(LineString など)はラベル生成対象外にする
      const geomType = polygonFeature.geometry?.type;
      if (geomType !== "Polygon" && geomType !== "MultiPolygon") {
        return false;
      }

      const roomName = typeof properties.room === "string" ? properties.room : "";
      const featureId = typeof properties.id === "string" ? properties.id : "";
      const category = typeof properties.category === "string" ? properties.category : "";
      const shapeType = typeof properties.shapeType === "string" ? properties.shapeType : "";

      // STAR は図形を描かず、テキストのみ表示したいので、room が空でも許可する
      const isStar = shapeType === "STAR";

      // ラベル非表示リストに含まれる場合は除外（STAR でも適用）
      if (!isStar) {
        // 通常は room 名が無いものは除外
        if (!roomName || LABEL_EXCLUDED_ROOM_SET.has(roomName)) {
          return false;
        }
      }

      if (LABEL_EXCLUDED_ID_SET.has(featureId)) {
        return false;
      }

      if (LABEL_EXCLUDED_CATEGORY_SET.has(category)) {
        return false;
      }

      // 階段ハッチ線専用カテゴリも強制除外（念のため）
      if (category === "stairs-hatch") {
        return false;
      }

      // 階段方向表示用の矢印ヘッドもラベル生成対象外
      if (category === "stairs-arrow-head") {
        return false;
      }

      // STAR は id ベースのラベル表示を許可、それ以外は room ベース
      return isStar ? featureId.length > 0 : true;
    })
    .map((polygonFeature) => {
      // pointOnFeatureでポリゴン内部の点を取得
      const labelPoint = pointOnFeature(polygonFeature) as LabelPointFeature;

      // テキストサイズと最小ズームレベルを設定
      const { textSize, minZoom } = getLabelSizing();

      const polygonProps = polygonFeature.properties ?? {};
      const roomName = typeof polygonProps.room === "string" ? polygonProps.room : "";
      const featureId = typeof polygonProps.id === "string" ? polygonProps.id : "";
      const baseLabel = roomName || featureId;

      // ラベル内に半角スペースが含まれる場合、スペース以降を改行する
      const displayRoom = baseLabel ? buildDisplayRoomLabel(baseLabel) : undefined;
      const suppressLabel =
        typeof polygonProps.suppress_label === "boolean" ? polygonProps.suppress_label : false;
      const isLowPriority = suppressLabel;
      const labelMinZoom = isLowPriority ? Math.max(minZoom, LOW_PRIORITY_LABEL_MIN_ZOOM) : minZoom;

      labelPoint.properties = {
        ...(polygonProps as CustomGeoJsonProperties),
        labelTextSize: textSize,
        labelMinZoom,
        isLowPriority,
        suppress_label: suppressLabel,
        ...(displayRoom ? { displayRoom } : {}),
      };

      return labelPoint;
    });

  return featureCollection(detailPoints);
};

// パン制限用のバウンディングボックスを計算
export const calculatePaddedBounds = (
  polygons?: FeatureCollection<Polygon | MultiPolygon>,
  paddingOptions: BoundsPaddingOptions = {},
) => {
  const ratioX = paddingOptions.ratioX ?? MAP_CONFIG.panPaddingRatio;
  const ratioY = paddingOptions.ratioY ?? MAP_CONFIG.panPaddingRatio;

  if (!polygons || polygons.features.length === 0) {
    const { scaleFactor, originalWidth, originalHeight } = calculateMapConfig();
    const minX = 0;
    const minY = 0;
    const maxX = originalWidth * scaleFactor;
    const maxY = originalHeight * scaleFactor;

    const width = maxX - minX;
    const height = maxY - minY;
    const paddingX = Math.max(MAP_CONFIG.panPadding, width * ratioX);
    const paddingY = Math.max(MAP_CONFIG.panPadding, height * ratioY);

    return [
      [minX - paddingX, minY - paddingY],
      [maxX + paddingX, maxY + paddingY],
    ] as [[number, number], [number, number]];
  }

  const [minX, minY, maxX, maxY] = bbox(polygons);

  const width = maxX - minX;
  const height = maxY - minY;

  const paddingX = Math.max(MAP_CONFIG.panPadding, width * ratioX);
  const paddingY = Math.max(MAP_CONFIG.panPadding, height * ratioY);

  // 余白を加えたバウンディングボックスを返す
  return [
    [minX - paddingX, minY - paddingY],
    [maxX + paddingX, maxY + paddingY],
  ] as [[number, number], [number, number]];
};

// ラベルのテキストサイズと最小ズームレベルを取得
const getLabelSizing = () => ({
  textSize: DEFAULT_ROOM_LABEL_TEXT_SIZE,
  minZoom: MAP_CONFIG.minZoom,
});

// 最初の半角スペースで文字列を分割して改行する
const buildDisplayRoomLabel = (label: string) => {
  if (!label.includes(" ")) {
    return undefined;
  }
  const trimmed = label.trim();
  const segments = trimmed.split(" ");
  if (segments.length <= 1) {
    return undefined;
  }
  return `${segments[0]}\n${segments.slice(1).join(" ")}`;
};
